import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { intraFreqX1HoCellChartConfig } from '../cellChartConfigs';

/**
 * Props for the IntraFreqX1HoCell component
 */
interface IntraFreqX1HoCellProps {
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
  /** Current fullscreen state */
  isFullscreen: boolean;
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
}

/**
 * Intra-Frequency X1 Handover Chart (LTE Cell Level)
 * 
 * Displays intra-frequency handover metrics over the X1 interface for individual LTE cells.
 * Shows handovers between cells operating on the same frequency within the same eNB or between eNBs.
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level intra-frequency X1 handover metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <IntraFreqX1HoCell
 *   cellName="173716_145"
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (intraFreqX1HoCellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/intra-freq-x1-ho?cell_name={cellName}
 */

const IntraFreqX1HoCell: React.FC<IntraFreqX1HoCellProps> = ({
  cellName,
  isFullscreen,
  onFullscreenToggle
}) => {
  const additionalParams = useMemo(
    () => ({
      cell_name: cellName
    }),
    [cellName]
  );

  return (
    <SiteCharts
      config={intraFreqX1HoCellChartConfig}
      additionalParams={additionalParams}
      isFullscreen={isFullscreen}
      onFullscreenToggle={onFullscreenToggle}
    />
  );
};

export default IntraFreqX1HoCell;
